import { toInternationalFormat } from "utils/Haim/phoneNumbersUtils";

/**
 * Phone helpers shared by every surface that turns a listing's seller into a
 * contact — the listings board, the listing form's seller section, the listing
 * details dialog, and the rental-to-sale table these were extracted from.
 *
 * They live apart from any component because the same two questions get asked in
 * three different renderers, and getting either one wrong writes bad data rather
 * than merely looking wrong.
 */

/**
 * Is this a Yad2 mask rather than the owner's own number?
 *
 * Israeli VoIP / virtual numbers use the 073 and 055 prefixes, and Yad2 has been
 * putting private sellers behind them. Both national (073XXXXXXX) and
 * international (+97273XXXXXXX) forms are checked, because the same number
 * reaches us in both shapes depending on which scrape tier fetched it.
 *
 * ⚠ This matters far more than a badge. A contact's Firestore document id IS its
 * international phone number, so saving a mask mints a contact that can never be
 * reached and that only the phone-re-key flow can repair. Every surface that can
 * create a contact from a listing must surface this BEFORE the write.
 *
 * Deliberately not a validity check: a masked number is a real, dialable number.
 * It is simply not the person's.
 */
export function isVirtualPhone(phone?: string): boolean {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, "");
  return (
    digits.startsWith("073") ||
    digits.startsWith("97273") ||
    digits.startsWith("055") ||
    digits.startsWith("97255")
  );
}

/**
 * Every plausible document id a contact with this phone could be stored under.
 *
 * Contact ids SHOULD all be E.164, and measured on the pilot org 47,726 of
 * 47,726 are phone-shaped — but years of client-side writes left several
 * spellings in play ("+972501234567", "972501234567", "0501234567"). A lookup
 * that only tries the canonical form reports "not a contact" for someone who is
 * one, and the caller then creates a duplicate.
 *
 * Capped at 10 by the caller's `where('__name__','in',…)` — Firestore's limit —
 * which this comfortably fits.
 */
export function getPhoneCandidates(phoneNumber: string): string[] {
  const candidates = new Set<string>();

  const stripped = phoneNumber.replace(/[\s\-+]/g, "");
  candidates.add(stripped);

  const international = toInternationalFormat(phoneNumber);
  if (international) {
    candidates.add(international); // e.g. "+972501234567"
    candidates.add(international.replace("+", "")); // e.g. "972501234567"
  }

  // Israeli local → international variants
  if (stripped.startsWith("0") && stripped.length === 10) {
    candidates.add("972" + stripped.slice(1));
    candidates.add("+972" + stripped.slice(1));
  }

  // International → local variant
  if (stripped.startsWith("972") && stripped.length === 12) {
    candidates.add("0" + stripped.slice(3));
    candidates.add("+" + stripped);
  }

  return Array.from(candidates);
}

/**
 * The contact document id to write under, or null when the input is not a phone
 * we can key on.
 *
 * Always the international form: it is what `EditContactDialog` writes, so
 * anything else here would create a SECOND contact for a person who already has
 * one under the canonical id.
 */
export function contactIdForPhone(phone?: string): string | null {
  if (!phone?.trim()) return null;
  return toInternationalFormat(phone.trim()) ?? null;
}

/**
 * Are these two spellings the same number?
 *
 * The one question the seller section asks that is NOT about writing: it decides
 * whether picking a contact actually DISCARDED the phone the realtor had typed,
 * and therefore whether to offer her an undo. Getting it wrong in the false
 * direction nags on every pick; in the true direction it silently drops her work.
 *
 * Compared on the canonical international form when both parse — that is the form
 * the contact's document id uses, so "050-123-4567" and "+972501234567" are one
 * number here exactly as they are one contact. When either side does not parse
 * (a partly-typed number, an extension, a foreign number we have no metadata for)
 * it falls back to comparing digits, which is strictly better than declaring two
 * identical strings different.
 *
 * Two blanks are NOT the same number — nothing was typed, so nothing was discarded.
 */
export function isSamePhone(left?: string, right?: string): boolean {
  const leftRaw = (left ?? "").trim();
  const rightRaw = (right ?? "").trim();
  if (!leftRaw || !rightRaw) return false;

  // Digits first, and not only as an optimisation: `toInternationalFormat` warns
  // on anything it cannot parse, and the commonest inputs here — two spellings of
  // the same typed string — need no parsing to be recognised.
  if (leftRaw.replace(/\D/g, "") === rightRaw.replace(/\D/g, "")) return true;

  const leftId = contactIdForPhone(leftRaw);
  const rightId = contactIdForPhone(rightRaw);
  return Boolean(leftId && rightId && leftId === rightId);
}
